import {
  Notification,
  prependNotification,
  setUnreadCount,
} from "@app/shared/atoms/notifications-atom";
import { playNotificationSound } from "@app/shared/utils/play-notification-sound";
import { AUTH_TOKEN_KEY } from "@core/account/utils";
import { __DEV__ } from "@framework/config/environment";
import { userAtom } from "@framework/config/atoms";
import cache from "@mongez/cache";
import { useEffect } from "react";
import { io, type Socket } from "socket.io-client";

// Mirror the dev/prod base URL resolution used by the HTTP endpoint
// (web/src/framework/remix/config/endpoint.ts) so realtime + REST stay aligned.
const SOCKET_URL = __DEV__
  ? "http://localhost:2029"
  : "https://api.feather.mentoor.io";

/**
 * Opens a browser-only Socket.IO connection authenticated with the current
 * student's access token and keeps the notifications atom in sync with the
 * realtime events emitted by the backend.
 *
 * SSR-safe: the connection is only ever created inside an effect, guarded by a
 * `typeof window` check, so it never runs during server rendering.
 */
export function useNotificationsSocket() {
  const user = userAtom.useValue();
  const accessToken = user.accessToken;
  const isUser = user.userType === "user";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!accessToken || !isUser) return;

    const socket: Socket = io(SOCKET_URL, {
      path: "/socket.io",
      auth: { token: accessToken },
      transports: ["websocket"],
    });

    const onNotification = (notification: Notification) => {
      const realtimeCreatedAt = notification.createdAt as
        | Notification["createdAt"]
        | string;
      const timestamp =
        typeof realtimeCreatedAt === "number"
          ? realtimeCreatedAt
          : typeof realtimeCreatedAt === "string"
            ? Date.parse(realtimeCreatedAt)
            : realtimeCreatedAt?.timestamp;

      prependNotification({
        ...notification,
        createdAt: Number.isFinite(timestamp) ? Number(timestamp) : Date.now(),
      });
      playNotificationSound();
    };

    const onUnreadCount = (count: number) => {
      setUnreadCount(count);
    };

    const onSuspended = async () => {
      cache.remove(AUTH_TOKEN_KEY);
      const abortController = new AbortController();
      const timeoutId = window.setTimeout(() => abortController.abort(), 3000);

      try {
        await fetch("/auth/logout", {
          method: "POST",
          signal: abortController.signal,
        });
      } catch {
        // A failed request must not keep a suspended account on screen.
      } finally {
        window.clearTimeout(timeoutId);
        window.location.replace("/auth/login?suspended=1");
      }
    };

    socket.on("notification", onNotification);
    socket.on("notification:unreadCount", onUnreadCount);
    socket.on("auth:suspended", onSuspended);

    return () => {
      socket.off("notification", onNotification);
      socket.off("notification:unreadCount", onUnreadCount);
      socket.off("auth:suspended", onSuspended);
      socket.disconnect();
    };
  }, [accessToken, isUser]);
}
